/**
 * LDS split payment SSOT — order.payments[i] maps 1:1 to card[i].
 * No sumpaid / activeCard / last-payment / sequential pool inference for amounts.
 */
import { safeJson } from "../orderByIdOfferMerge.js";

function round2(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

function toNum(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function truthyPaid(raw) {
  return raw === true || raw === "true" || raw === 1 || raw === "1";
}

function isPaymentSlotLedgerPaid(payment) {
  if (!payment || typeof payment !== "object") return false;
  if (truthyPaid(payment.paid ?? payment.Paid)) return true;
  const status = String(payment.status ?? payment.Status ?? "")
    .trim()
    .toLowerCase();
  if (
    status === "paid" ||
    status === "captured" ||
    status === "success" ||
    status === "completed"
  ) {
    return true;
  }
  const callbackAt = payment.callback_received_at ?? payment.callbackReceivedAt;
  return callbackAt != null && String(callbackAt).trim() !== "";
}

/** Parse payments[] from order row (may be JSON string). */
export function parseLdsOrderPayments(order) {
  if (!order || typeof order !== "object") return [];
  let raw = order.payments;
  if (raw == null) return [];
  if (typeof raw === "string") {
    raw = safeJson(raw, []);
  }
  if (!Array.isArray(raw)) return [];
  return raw.filter((p) => p && typeof p === "object");
}

/** Which field supplied the slot amount (for DEV trace). */
export function resolveLdsPaymentSlotAmountSource(payment) {
  if (!payment || typeof payment !== "object") {
    return { amount: 0, sourceField: "none" };
  }
  const cumulative = toNum(
    payment.cumulative_captured_ils ?? payment.cumulativeCapturedIls
  );
  if (cumulative > 0) {
    return { amount: round2(cumulative), sourceField: "cumulative_captured_ils" };
  }
  const captured = toNum(
    payment.captured_amount_ils ?? payment.capturedAmountIls
  );
  if (captured > 0) {
    return { amount: round2(captured), sourceField: "captured_amount_ils" };
  }
  if (payment.j5_capture_state === "partial_captured") {
    const partial = toNum(
      payment.actualAmount ??
        payment.actual_amount ??
        payment.Amount ??
        payment.amount
    );
    if (partial > 0) {
      return { amount: round2(partial), sourceField: "j5_partial:actualAmount" };
    }
  }
  if (isPaymentSlotLedgerPaid(payment)) {
    const paidAmt = toNum(
      payment.actualAmount ??
        payment.actual_amount ??
        payment.Amount ??
        payment.amount
    );
    if (paidAmt > 0) {
      return { amount: round2(paidAmt), sourceField: "paid:actualAmount" };
    }
  }
  const intended = toNum(
    payment.actualAmount ??
      payment.actual_amount ??
      payment.Amount ??
      payment.amount
  );
  if (intended > 0) {
    return { amount: round2(intended), sourceField: "amount" };
  }
  return { amount: 0, sourceField: "none" };
}

/** Captured/paid ILS for payments[i] — direct fields only (no sumpaid pool). */
export function resolveLdsPaymentSlotCapturedStrict(payment, order = null, index = 0) {
  if (!payment || typeof payment !== "object") return 0;
  const cumulative = toNum(
    payment.cumulative_captured_ils ?? payment.cumulativeCapturedIls
  );
  if (cumulative > 0) return round2(cumulative);
  const captured = toNum(
    payment.captured_amount_ils ?? payment.capturedAmountIls
  );
  if (captured > 0) return round2(captured);
  if (payment.j5_capture_state === "partial_captured") {
    return round2(
      toNum(
        payment.actualAmount ??
          payment.actual_amount ??
          payment.Amount ??
          payment.amount
      )
    );
  }
  if (isPaymentSlotLedgerPaid(payment)) {
    return round2(
      toNum(
        payment.actualAmount ??
          payment.actual_amount ??
          payment.Amount ??
          payment.amount
      )
    );
  }
  // Unpaid link with stale actualAmount — slot `amount` is the per-slot capture.
  if (payment.url) {
    const slotAmt = toNum(payment.amount ?? payment.Amount);
    const actualAmt = toNum(
      payment.actualAmount ?? payment.actual_amount ?? payment.Amount
    );
    if (slotAmt > 0 && actualAmt > slotAmt) {
      return round2(slotAmt);
    }
  }
  return 0;
}

function isBankTransferPaymentSlot(payment) {
  if (!payment || typeof payment !== "object") return false;
  const norm = (v) =>
    String(v || "")
      .trim()
      .toLowerCase()
      .replace(/-/g, "_")
      .replace(/\s+/g, "_");
  const pm = norm(payment.payment_method ?? payment.paymentMethod);
  const pp = norm(payment.payment_provider);
  if (pm === "bank_transfer" || pm === "banktransfer") return true;
  if (
    pp === "bank_transfer" ||
    pp === "banktransfer" ||
    pp === "admin_bank_transfer_finalize"
  ) {
    return true;
  }
  return pm.includes("bank_transfer") || pp.includes("bank_transfer");
}

function hasPendingBankTransferSettlement(order) {
  if (!order || typeof order !== "object") return false;
  const audit = order.post_payment_checkout_audit ?? order.postPaymentCheckoutAudit;
  const sd =
    audit && typeof audit === "object"
      ? audit.settlement_decision ?? audit.settlementDecision
      : null;
  if (!sd || typeof sd !== "object") return false;
  const method = String(sd.method || "").trim();
  const remaining = Number(sd.remaining_ils ?? sd.remainingIls);
  return method === "bank_transfer_remaining" && Number.isFinite(remaining) && remaining > 0;
}

function paymentSettlementIdentityKeys(payment) {
  const keys = new Set();
  const sid = String(
    payment?.payment_session_id ?? payment?.paymentAttemptId ?? ""
  ).trim();
  if (sid) keys.add(`session:${sid}`);
  for (const field of ["payment_id", "Id", "transaction_id", "transactionId"]) {
    const v = String(payment?.[field] ?? "").trim();
    if (v) keys.add(`txn:${v}`);
  }
  const auth = String(
    payment?.AuthNumber ?? payment?.authNumber ?? payment?.ACode ?? payment?.aCode ?? ""
  ).trim();
  if (auth) keys.add(`auth:${auth}`);
  const ccode = String(payment?.CCode ?? payment?.ccode ?? "").trim();
  if (ccode && ccode !== "000" && ccode !== "0") keys.add(`ccode:${ccode}`);
  const src = String(payment?.source ?? "").trim();
  if (src === "additional_order_payment_link" && sid) keys.add(`additional:${sid}`);
  return keys;
}

function paymentDedupeRank(payment) {
  if (truthyPaid(payment?.paid ?? payment?.Paid)) return 100;
  if (String(payment?.source ?? "").trim() === "additional_order_payment_link") return 60;
  if (payment?.j5_capture_state === "partial_captured") return 20;
  return 10;
}

/** Sum captured/paid across payment slots — SSOT paid total (card capture; not pending bank). */
export function sumLdsPaymentsCapturedStrict(payments, order = null) {
  if (!Array.isArray(payments) || payments.length === 0) return 0;
  const excludePendingBank = hasPendingBankTransferSettlement(order);
  const slots = [];
  for (let i = 0; i < payments.length; i++) {
    const captured = resolveLdsPaymentSlotCapturedStrict(payments[i], order, i);
    if (captured <= 0) continue;
    slots.push({ index: i, payment: payments[i], captured });
  }
  slots.sort(
    (a, b) =>
      paymentDedupeRank(b.payment) - paymentDedupeRank(a.payment) ||
      a.index - b.index
  );

  const seenKeys = new Set();
  let card = 0;
  let bank = 0;
  for (const { payment, captured } of slots) {
    const keys = paymentSettlementIdentityKeys(payment);
    if (keys.size > 0) {
      let dup = false;
      for (const k of keys) {
        if (seenKeys.has(k)) {
          dup = true;
          break;
        }
      }
      if (dup) continue;
      for (const k of keys) seenKeys.add(k);
    }
    if (isBankTransferPaymentSlot(payment)) {
      bank += captured;
    } else {
      card += captured;
    }
  }
  if (excludePendingBank) {
    return round2(card);
  }
  return round2(card + bank);
}

export function normalizeLdsPaymentsForTrace(payments, order = null) {
  if (!Array.isArray(payments)) return [];
  return payments.map((p, index) => {
    const { amount, sourceField } = resolveLdsPaymentSlotAmountSource(p);
    const captured = resolveLdsPaymentSlotCapturedStrict(p, order, index);
    return {
      index,
      amount: captured > 0 ? captured : amount,
      status: p?.j5_capture_state
        ? String(p.j5_capture_state)
        : truthyPaid(p?.paid ?? p?.Paid)
          ? "paid"
          : p?.url
            ? "link"
            : "open",
      sourceField: captured > 0 ? `${sourceField}(captured)` : sourceField,
    };
  });
}

function isDevRuntime() {
  if (typeof import.meta !== "undefined" && import.meta?.env?.DEV) return true;
  if (typeof process !== "undefined" && process?.env?.NODE_ENV) {
    return process.env.NODE_ENV !== "production";
  }
  return false;
}

/** DEV-only trace — [LDS_SPLIT_PAYMENTS_SSOT_TRACE] */
export function traceLdsSplitPaymentsSsot({
  order,
  builtCards = [],
  activeCardIndex = -1,
  orderTotal = null,
}) {
  if (!isDevRuntime()) return;
  try {
    const rawPayments = parseLdsOrderPayments(order);
    const paidFromPayments = sumLdsPaymentsCapturedStrict(rawPayments);
    const total =
      orderTotal != null && Number.isFinite(Number(orderTotal))
        ? round2(Number(orderTotal))
        : null;
    const remainingFromPayments =
      total != null ? round2(Math.max(total - paidFromPayments, 0)) : null;

    // eslint-disable-next-line no-console
    console.info("[LDS_SPLIT_PAYMENTS_SSOT_TRACE]", {
      orderId: String(order?.id ?? order?._id ?? ""),
      orderTotal: total,
      rawPaymentsLength: rawPayments.length,
      rawPayments,
      normalizedPayments: normalizeLdsPaymentsForTrace(rawPayments, order),
      paidFromPayments,
      remainingFromPayments,
      builtCards: (builtCards || []).map((c, index) => ({
        index,
        amount: round2(Number(c?.amount) || 0),
        locked: Boolean(c?.ldsSlotLocked || c?.paid),
        paymentIndex:
          c?.ldsPaymentSlotIndex != null ? c.ldsPaymentSlotIndex : index,
        source: c?.ldsSlotLocked ? "payments[]" : "synthetic",
      })),
      activeCardIndex,
    });
  } catch {
    /* ignore */
  }
}
